import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  level?: 'page' | 'component' | 'minor';
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

// Higher-order component to add navigation to error boundary
const withNavigation = <P extends object>(Component: React.ComponentType<P>) => {
  return (props: P) => {
    const navigate = useNavigate();
    return <Component {...props} navigate={navigate} />;
  };
};

class SafeErrorBoundaryComponent extends Component<Props & { navigate?: (path: string) => void }, State> {
  private retryCount = 0;
  private maxRetries = 3;

  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    errorId: ''
  };

  public static getDerivedStateFromError(error: Error): State {
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return { 
      hasError: true, 
      error, 
      errorInfo: null,
      errorId
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`SafeErrorBoundary [${this.state.errorId}]:`, error, errorInfo);
    
    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
    
    // Track error for monitoring
    this.trackError(error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });
  }

  private trackError = (error: Error, errorInfo: ErrorInfo) => {
    // In a real app, send to error tracking service like Sentry
    const errorData = {
      errorId: this.state.errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      level: this.props.level || 'component',
      retryCount: this.retryCount
    };
    
    // Store in localStorage for debugging
    const errors = JSON.parse(localStorage.getItem('app_errors') || '[]');
    errors.push(errorData);
    localStorage.setItem('app_errors', JSON.stringify(errors.slice(-50))); // Keep last 50
  };

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    });
    this.retryCount = 0;
  };

  private handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      this.handleReset();
    }
  };

  private handleGoHome = () => {
    this.props.navigate?.('/');
    this.handleReset();
  };

  public render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { level = 'component' } = this.props;
      const canRetry = this.retryCount < this.maxRetries;

      // Minor errors - minimal UI
      if (level === 'minor') {
        return (
          <div className="flex items-center justify-center p-4 text-muted-foreground">
            <AlertTriangle className="h-4 w-4 mr-2" />
            <span className="text-sm">Something went wrong</span>
            {canRetry && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={this.handleRetry}
                className="ml-2"
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
            )}
          </div>
        );
      }

      // Component level errors
      if (level === 'component') {
        return (
          <Card className="w-full max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive text-base">
                <AlertTriangle className="h-4 w-4" />
                Component Error
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                This component encountered an error. You can try reloading it.
              </p>
              
              <div className="flex gap-2">
                {canRetry && (
                  <Button onClick={this.handleRetry} size="sm" className="flex-1">
                    <RefreshCw className="h-3 w-3 mr-2" />
                    Retry
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={() => window.location.reload()}
                  size="sm"
                  className="flex-1"
                >
                  Reload Page
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      }

      // Page level errors - full error UI
      return (
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Something went wrong
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.
              </p>
              
              <div className="p-3 bg-muted rounded-lg text-xs">
                <p className="font-medium text-muted-foreground">Error ID: {this.state.errorId}</p>
                {this.retryCount > 0 && (
                  <p className="text-muted-foreground">Retry attempts: {this.retryCount}/{this.maxRetries}</p>
                )}
              </div>
              
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="p-3 bg-muted rounded text-xs font-mono">
                  <summary className="cursor-pointer font-semibold mb-2">Error Details (Dev)</summary>
                  <div className="space-y-2">
                    <div>
                      <strong>Message:</strong> {this.state.error.message}
                    </div>
                    {this.state.error.stack && (
                      <div>
                        <strong>Stack:</strong>
                        <pre className="mt-1 text-xs whitespace-pre-wrap">
                          {this.state.error.stack}
                        </pre>
                      </div>
                    )}
                    {this.state.errorInfo?.componentStack && (
                      <div>
                        <strong>Component Stack:</strong>
                        <pre className="mt-1 text-xs whitespace-pre-wrap">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}
              
              <div className="flex gap-2">
                {canRetry && (
                  <Button onClick={this.handleRetry} className="flex-1">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  onClick={() => window.location.reload()}
                  className="flex-1"
                >
                  Reload Page
                </Button>
                {this.props.navigate && (
                  <Button 
                    variant="outline" 
                    onClick={this.handleGoHome}
                    className="flex-1"
                  >
                    <Home className="h-4 w-4 mr-2" />
                    Home
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export const SafeErrorBoundary = withNavigation(SafeErrorBoundaryComponent);

// Convenience wrapper for minor errors
export const MinorErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <SafeErrorBoundary level="minor">
    {children}
  </SafeErrorBoundary>
);

// Re-export for backwards compatibility
export { useErrorReporting } from '@/hooks/useErrorReporting';